import { useCallback, useState } from 'react';

import { NewsList } from '../../components/news/NewsList/NewsList';
import { NewsPreview } from '../../components/news/NewsPreview/NewsPreview';
import { NewsFormModal } from '../../components/news/NewsFormModal/NewsFormModal';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Button } from '../../components/ui/Button/Button';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { useToast } from '../../components/ui/Toast/ToastProvider';

import {
    useCreateArticleMutation,
    useDeleteArticleMutation,
    useGetNewsHandbookQuery,
    useUpdateArticleMutation,
} from '../../entities/handbook/api/newsApi';
import { useNewsHandbook } from '../../hooks/useNewsHandbook';
import type { NewsItem } from '../../types/news';
import { htmlToPlainText, paragraphsToHtml } from '../../utils/news';
import { plural } from '../../utils/plural';
import styles from './NewsHandbookPage.module.scss';
import React from 'react';

type Dialog = { kind: 'create' } | { kind: 'edit'; item: NewsItem } | null;

export function NewsHandbookPage() {
    const notify = useToast();

    const { data: news = [], isLoading } = useGetNewsHandbookQuery();
    const handbook = useNewsHandbook(news);

    const [createArticle, { isLoading: isCreating }] = useCreateArticleMutation();
    const [updateArticle, { isLoading: isUpdating }] = useUpdateArticleMutation();
    const [deleteArticle, { isLoading: isDeleting }] = useDeleteArticleMutation();

    const [dialog, setDialog] = useState<Dialog>(null);
    const [removing, setRemoving] = useState<NewsItem | null>(null);

    const closeDialog = useCallback(() => setDialog(null), []);

    const handleSubmit = useCallback(
        async ({ title, text, file = null }: { title: string; text: string; file?: File | null }) => {
            const form = { title, description: paragraphsToHtml(text), file };

            try {
                if (dialog?.kind === 'edit') {
                    await updateArticle({ id: dialog.item.id, form }).unwrap();
                    notify('Новость обновлена');
                } else {
                    await createArticle(form).unwrap();
                    notify('Новость создана');
                }
                closeDialog();
            } catch {
                /* тост об ошибке уже показал notifyOnError */
            }
        },
        [dialog, createArticle, updateArticle, notify, closeDialog],
    );

    const confirmRemove = useCallback(async () => {
        if (!removing) return;
        try {
            await deleteArticle(removing.id).unwrap();
            notify('Новость удалена');
            setRemoving(null);
        } catch {
            /* тост показал notifyOnError */
        }
    }, [removing, deleteArticle, notify]);

    const total = handbook.total;

    return (
        <div className={styles.page}>
            <PageHeader
                title="Новости"
                meta={
                    isLoading
                        ? ['Загрузка справочника...']
                        : [
                            <>
                                <b>{total}</b> {plural(total, 'новость', 'новости', 'новостей')}
                            </>,
                        ]
                }
                actions={
                    <Button variant="primary" onClick={() => setDialog({ kind: 'create' })}>
                        Создать новость
                    </Button>
                }
            />

            <div className={styles.split}>
                <NewsList
                    items={handbook.visible}
                    activeId={handbook.activeItem?.id ?? null}
                    query={handbook.query}
                    isNewestFirst={handbook.isNewestFirst}
                    isFiltered={handbook.isFiltered}
                    onQueryChange={handbook.setQuery}
                    onToggleOrder={handbook.toggleOrder}
                    onSelect={handbook.selectNews}
                    onReset={handbook.reset}
                />

                <NewsPreview
                    item={handbook.activeItem}
                    onEdit={(item) => setDialog({ kind: 'edit', item })}
                    onDelete={(item) => setRemoving(item)}
                />
            </div>

            <NewsFormModal
                open={dialog !== null}
                initialTitle={dialog?.kind === 'edit' ? dialog.item.title : ''}
                initialText={dialog?.kind === 'edit' ? htmlToPlainText(dialog.item.html) : ''}
                submitting={isCreating || isUpdating}
                onClose={closeDialog}
                onSubmit={handleSubmit}
            />

            <ConfirmModal
                open={removing !== null}
                danger
                title={`Удалить новость «${removing?.title ?? ''}»?`}
                description="Новость пропадёт из ленты у всех пользователей. Отменить нельзя."
                confirmLabel="Удалить"
                busy={isDeleting}
                onConfirm={confirmRemove}
                onClose={() => setRemoving(null)}
            />
        </div>
    );
}